import * as dateFns from 'date-fns';

export const DATE_DOT_FORMAT = 'yyyy.MM.dd';
export const DATE_COMPACT_FORMAT = 'MM/d/yy';
export const DATE_EUROPEAN_MONTH_FORMAT = 'MM/dd';
export const DATE_TRADITIONAL_FORMAT = 'MM/dd/yyyy';

export const TIME_AM_PM_FORMAT = 'h:mm a';
export const TIME_SECONDS_AM_PM_FORMAT = 'h:mm:ss a';
export const TIME_SECONDS_AM_PM_ALT_FORMAT = 'hh:mm:ssa';

export const DATE_TIME_YEAR_24_HOUR_SECONDS_FORMAT = 'yyyy/MM/dd HH:mm:ss';
export const DATE_TIME_EXTENDED_FORMAT = "MM/dd/yyyy 'at' h:mm a";
export const DATE_TIME_EXTENDED_FULL_FORMAT = "MMMM do yyyy 'at' h:mm a";
export const DATE_TIME_EXTENDED_SECONDS_FORMAT = 'MMMM do yyyy, h:mm:ss a';
export const DATE_TIME_COMPACT_FORMAT = 'MM/d/yy h:mm a';

type DateType = number | Date | undefined | null;
type ModifyKeys =
  | 'subMinutes'
  | 'subDays'
  | 'subWeeks'
  | 'subMonths'
  | 'subYears'
  | 'addMinutes'
  | 'addDays';

const isInvalidDate = (date: DateType): boolean => {
  return !date || (date instanceof Date && isNaN(date.valueOf()));
};

const modifyDate = (key: ModifyKeys) => (
  date: DateType,
  amount: number
): Date => {
  if (isInvalidDate(date)) {
    return date instanceof Date ? date : new Date(date!);
  }

  return dateFns[key](date!, amount);
};

export const subMinutes = modifyDate('subMinutes');
export const subDays = modifyDate('subDays');
export const subWeeks = modifyDate('subWeeks');
export const subMonths = modifyDate('subMonths');
export const subYears = modifyDate('subYears');
export const addMinutes = modifyDate('addMinutes');
export const addDays = modifyDate('addDays');

export const differenceInDays = (
  dateLeft: DateType,
  dateRight: DateType
): number => {
  if (isInvalidDate(dateLeft) || isInvalidDate(dateRight)) {
    return 0;
  }

  return dateFns.differenceInDays(dateLeft!, dateRight!);
};

export const isAfter = (dateLeft: DateType, dateRight: DateType): boolean => {
  if (isInvalidDate(dateLeft) || isInvalidDate(dateRight)) {
    return false;
  }

  return dateFns.isAfter(dateLeft!, dateRight!);
};

export const formatDistance = (
  date: DateType,
  baseDate: number | Date,
  addSuffix: boolean | undefined = true
): string => {
  if (isInvalidDate(date)) {
    return 'Invalid Date';
  }

  return dateFns.formatDistance(date!, baseDate, {
    includeSeconds: true,
    addSuffix,
  });
};

export const formatRelative = (
  date: DateType,
  baseDate: number | Date
): string => {
  if (isInvalidDate(date)) {
    return 'Invalid Date';
  }

  return dateFns.formatRelative(date!, baseDate);
};

export const startOfDay = (date: DateType): Date => {
  if (isInvalidDate(date)) {
    return date instanceof Date ? date : new Date(date!);
  }

  return dateFns.startOfDay(date!);
};

export const endOfDay = (date: DateType): Date => {
  if (isInvalidDate(date)) {
    return date instanceof Date ? date : new Date(date!);
  }

  return dateFns.endOfDay(date!);
};

export const getTime = (date: DateType): number => {
  if (isInvalidDate(date)) {
    return 0;
  }

  return dateFns.getTime(date!);
};

export const parseISO = (argument: string) => {
  return dateFns.parseISO(argument);
};

export const format = (
  date: DateType,
  format: string,
  invalidMessage: string = 'Invalid Date'
): string => {
  if (isInvalidDate(date)) {
    return invalidMessage;
  }

  return dateFns.format(date!, format);
};
